<template>
  <!-- Listado de documentación: registra documentos con un código autogenerado
       (Emisor-SAP-Disciplina-CódigoTipo-Número). El número correlativo es por combinación del código.
       El PDF de cada fila se sube versionado (mismo componente que el resto del Expediente). -->
  <div class="doc-list" :class="{ mark }">
    <!-- Panel: alta de emisores externos (suministradores / subcontratistas) con código correlativo. -->
    <div class="dl-panel gray">
      <div class="dl-field">
        <label>{{ $t('prm_summary_s04_ext_issuer') }}</label>
        <select v-model="newIssuerType" class="dl-ctrl">
          <option v-for="t in issuerTypes" :key="t.value" :value="t.value">
            {{ $t(t.labelKey) }} ({{ nextIssuerCodeFor(t.prefix) }}…)
          </option>
        </select>
      </div>
      <div class="dl-field name">
        <label>{{ $t('prm_summary_s04_issuer_name') }}</label>
        <input v-model="newIssuerName" class="dl-ctrl" type="text" :placeholder="$t('prm_summary_s04_issuer_name_ph')"
               @keydown.enter="addIssuer">
      </div>
      <button class="dl-btn ghost" :disabled="!newIssuerName.trim()" @click="addIssuer">
        <b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_register_issuer') }}</span>
      </button>

      <!-- Desplegable de emisores registrados con opción de borrar cada uno. -->
      <div class="dl-reg">
        <button class="dl-btn ghost" @click="issuersOpen = !issuersOpen">
          <b-icon pack="fas" icon="magnifying-glass" size="is-small"></b-icon>
          <span>{{ $t('prm_summary_s04_registered_issuers') }} ({{ localIssuers.length }})</span>
          <b-icon pack="fas" icon="chevron-down" size="is-small"></b-icon>
        </button>
        <template v-if="issuersOpen">
          <div class="dl-backdrop" @click="issuersOpen = false"></div>
          <div class="dl-menu">
            <div v-for="(e, i) in localIssuers" :key="e.id" class="dl-menu-item">
              <span class="dl-menu-code">{{ e.code }}</span>
              <span class="dl-menu-name">{{ e.name }}</span>
              <button class="dl-trash" v-tip="$t('prm_summary_s04_delete')" @click="removeIssuer(i)">
                <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
              </button>
            </div>
            <div v-if="!localIssuers.length" class="dl-menu-empty">{{ $t('prm_summary_empty') }}</div>
          </div>
        </template>
      </div>
    </div>

    <!-- Panel: alta de un documento (emisor con selector secundario según el tipo, disciplina y tipo). -->
    <div class="dl-panel">
      <div class="dl-field">
        <label>{{ $t('prm_summary_s04_col_issuer') }}</label>
        <select v-model="issuerKind" class="dl-ctrl" @change="issuerRef = ''">
          <option value="TKAS">TKAS</option>
          <option value="client">{{ $t('prm_summary_s04_issuer_client') }}</option>
          <option value="external">{{ $t('prm_summary_s04_issuer_external') }}</option>
        </select>
      </div>
      <div v-if="issuerKind === 'client'" class="dl-field grow">
        <label>&nbsp;</label>
        <select v-model="issuerRef" class="dl-ctrl">
          <option value="">{{ $t('prm_summary_s04_select_client') }}</option>
          <option v-for="o in clientOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </div>
      <div v-else-if="issuerKind === 'external'" class="dl-field grow">
        <label>&nbsp;</label>
        <select v-model="issuerRef" class="dl-ctrl">
          <option value="">{{ $t('prm_summary_s04_select_issuer') }}</option>
          <option v-for="e in localIssuers" :key="e.id" :value="e.code">{{ e.code }} · {{ e.name }}</option>
        </select>
      </div>
      <div class="dl-field grow">
        <label>{{ $t('prm_summary_s04_col_discipline') }}</label>
        <select v-model="disciplineCode" class="dl-ctrl">
          <option value="">{{ $t('prm_summary_s04_select_discipline') }}</option>
          <option v-for="o in disciplineOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </div>
      <div class="dl-field grow">
        <label>{{ $t('prm_summary_s04_col_doctype') }}</label>
        <select v-model="docTypeCode" class="dl-ctrl">
          <option value="">{{ $t('prm_summary_s04_select_doctype') }}</option>
          <option v-for="o in docTypeOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </div>
      <!-- Adjuntar el PDF ya en el alta (se sube a la fila al registrar). -->
      <div class="dl-attach">
        <button type="button" class="dl-btn ghost" @click="pickStaged">
          <b-icon pack="fas" icon="paperclip" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_attach') }}</span>
        </button>
        <input ref="stagedInput" type="file" accept="application/pdf" class="dl-hidden" @change="onStaged">
        <span v-if="stagedFile" class="dl-staged">
          <b-icon pack="fas" icon="file-pdf" size="is-small"></b-icon>
          <span class="dl-staged-name" v-tip="stagedFile.name">{{ stagedFile.name }}</span>
          <button type="button" class="dl-staged-x" @click="clearStaged"><b-icon pack="fas" icon="xmark" size="is-small"></b-icon></button>
        </span>
      </div>
      <button class="dl-btn primary" :disabled="!canRegister || registering" @click="addDoc">
        <b-icon pack="fas" :icon="registering ? 'spinner' : 'plus'" :custom-class="registering ? 'fa-spin' : ''" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_register') }}</span>
      </button>
    </div>

    <!-- Sub-card: filtros + tabla de registros. -->
    <div class="dl-subcard">
      <div class="dl-filter-bar">
        <div class="dl-filter-grid">
          <div class="dl-field">
            <label>{{ $t('prm_summary_s04_search_label') }}</label>
            <div class="dl-search">
              <b-icon pack="fas" icon="magnifying-glass" size="is-small" class="dl-search-ic"></b-icon>
              <input v-model="search" class="dl-ctrl srch" type="text" :placeholder="$t('prm_summary_s04_search_records')">
            </div>
          </div>
          <div class="dl-field">
            <label>{{ $t('prm_summary_s04_col_issuer') }}</label>
            <select v-model="filterIssuer" class="dl-ctrl">
              <option value="">{{ $t('prm_summary_s04_all_issuers') }}</option>
              <option v-for="c in issuerFilterCodes" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="dl-field">
            <label>{{ $t('prm_summary_s04_col_discipline') }}</label>
            <select v-model="filterDiscipline" class="dl-ctrl">
              <option value="">{{ $t('prm_summary_s04_all_disciplines') }}</option>
              <option v-for="o in disciplineOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <div class="dl-field">
            <label>{{ $t('prm_summary_s04_col_doctype') }}</label>
            <select v-model="filterDocType" class="dl-ctrl">
              <option value="">{{ $t('prm_summary_s04_all_doctypes') }}</option>
              <option v-for="o in docTypeOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <button class="dl-clear" :disabled="!hasFilters" @click="clearFilters">
            <b-icon pack="fas" icon="xmark" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_clear_filters') }}</span>
          </button>
        </div>
        <p class="dl-count">{{ filteredRows.length }} {{ $t('prm_summary_of') }} {{ localRows.length }} {{ $t('prm_summary_records') }}</p>
      </div>

      <div class="dl-table-scroll">
        <table>
          <thead>
            <tr>
              <th>{{ $t('prm_summary_s04_col_issuer') }}</th>
              <th>{{ $t('prm_summary_s04_col_project_code') }}</th>
              <th>{{ $t('prm_summary_s04_col_discipline') }}</th>
              <th>{{ $t('prm_summary_s04_col_type_code') }}</th>
              <th>{{ $t('prm_summary_s04_col_number') }}</th>
              <th>{{ $t('prm_summary_s04_col_full_code') }}</th>
              <th>{{ $t('prm_summary_s04_col_doctype') }}</th>
              <th>{{ $t('prm_summary_s04_col_file_versions') }}</th>
              <th>{{ $t('prm_summary_s03_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in filteredRows">
            <tr :key="row.id">
              <td>{{ row.issuerCode }}</td>
              <td>{{ sap }}</td>
              <td>{{ row.disciplineCode }}</td>
              <td>{{ row.docTypeCode }}</td>
              <td>{{ row.number }}</td>
              <td class="code">{{ fullCode(row) }}</td>
              <td>{{ docTypeName(row.docTypeCode) }}</td>
              <td>
                <prm-versioned-file :file="fileFor(row)" :editing="true" :project-id="projectId"
                  :open="historyRow === row.id" :upload-url="uploadUrl(row)"
                  @uploaded="onFileUploaded" @toggle-history="toggleHistory(row.id)"></prm-versioned-file>
              </td>
              <td class="acts">
                <button class="dl-copy" @click="copyCode(row)">{{ $t('prm_summary_s04_copy') }}</button>
                <button class="dl-del" @click="removeRow(row)"><b-icon pack="fas" icon="trash-can" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_delete') }}</span></button>
              </td>
            </tr>
            <tr v-if="historyRow === row.id && fileFor(row)" :key="row.id + '-h'" class="hist-tr">
              <td :colspan="9"><prm-file-history :file="fileFor(row)" :base-url="baseUrl" :project-id="projectId" @close="historyRow = null"></prm-file-history></td>
            </tr>
            </template>
            <tr v-if="!filteredRows.length"><td :colspan="9" class="empty">{{ $t('prm_summary_s04_no_docs') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import FileUploader from '~/plugins/fileUploader'
  import { clientCodeOptions } from '../shared/airports'
  import { disciplineOptions, docTypeOptions, docTypeName, ISSUER_EXTERNAL_TYPES, nextIssuerCode } from '../shared/doc-catalogs'
  import { tip } from '../shared/tip'

  let seq = 0
  function genId () { return 'r' + Date.now().toString(36) + (seq++).toString(36) + Math.random().toString(36).slice(2, 6) }

  export default {
    name: 'PrmDocList',

    components: { PrmVersionedFile, PrmFileHistory },

    directives: { tip },

    props: {
      rows: { type: Array, default: () => [] },       // s04.docList
      issuers: { type: Array, default: () => [] },    // s04.issuers (emisores externos)
      sap: { type: String, default: '' },
      projectId: { type: [Number, String], default: null },
      baseUrl: { type: String, default: '' },
      files: { type: Object, default: () => ({}) },   // sources.contractFiles.doc_list
      mark: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    data: () => ({
      localRows: [],
      localIssuers: [],
      newIssuerType: 'supplier',
      newIssuerName: '',
      issuersOpen: false,
      issuerKind: 'TKAS',
      issuerRef: '',
      disciplineCode: '',
      docTypeCode: '',
      search: '',
      filterIssuer: '',
      filterDiscipline: '',
      filterDocType: '',
      historyRow: null,
      stagedFile: null, // PDF adjuntado en el alta, se sube al registrar
      registering: false,
    }),

    computed: {
      issuerTypes () { return ISSUER_EXTERNAL_TYPES },
      clientOptions () { return clientCodeOptions() },
      disciplineOpts () { return disciplineOptions() },
      docTypeOpts () { return docTypeOptions() },
      // Código del emisor según el tipo elegido: TKAS fijo, o el código secundario (cliente/externo).
      issuerCode () { return this.issuerKind === 'TKAS' ? 'TKAS' : this.issuerRef },
      canRegister () {
        return !!this.issuerCode && !!this.disciplineCode && !!this.docTypeCode
      },
      hasFilters () { return !!(this.search || this.filterIssuer || this.filterDiscipline || this.filterDocType) },
      // Códigos de emisor presentes en los registros (para el filtro).
      issuerFilterCodes () {
        return [...new Set(this.localRows.map((r) => r.issuerCode).filter(Boolean))].sort()
      },
      filteredRows () {
        const q = this.search.trim().toLowerCase()
        return this.localRows.filter((r) => {
          if (this.filterIssuer && r.issuerCode !== this.filterIssuer) return false
          if (this.filterDiscipline && r.disciplineCode !== this.filterDiscipline) return false
          if (this.filterDocType && r.docTypeCode !== this.filterDocType) return false
          if (q) {
            const hay = (this.fullCode(r) + ' ' + docTypeName(r.docTypeCode)).toLowerCase()
            if (!hay.includes(q)) return false
          }
          return true
        })
      },
    },

    watch: {
      resetKey: { immediate: true, handler () { this.init() } },
    },

    methods: {
      docTypeName,

      init () {
        this.localRows = (this.rows || []).map((r) => ({ ...r, id: r.id || genId() }))
        this.localIssuers = (this.issuers || []).map((e) => ({ ...e, id: e.id || genId() }))
      },

      nextIssuerCodeFor (prefix) { return nextIssuerCode(this.localIssuers, prefix) },

      // Código completo = Emisor-SAP-Disciplina-CódigoTipo-Número.
      fullCode (row) {
        return [row.issuerCode, this.sap, row.disciplineCode, row.docTypeCode, row.number].join('-')
      },

      fileFor (row) { return this.files[row.id] || null },
      uploadUrl (row) { return `${this.baseUrl}/contract-files/doc_list/${row.id}` },
      onFileUploaded () { this.$emit('files-changed') },
      toggleHistory (rowId) { this.historyRow = this.historyRow === rowId ? null : rowId },

      // Número correlativo por combinación (emisor+disciplina+tipo): si ya existe, +1; si no, 0001.
      nextNumber (issuerCode, disciplineCode, docTypeCode) {
        let max = 0
        for (const r of this.localRows) {
          if (r.issuerCode === issuerCode && r.disciplineCode === disciplineCode && r.docTypeCode === docTypeCode) {
            const n = parseInt(r.number, 10) || 0
            if (n > max) max = n
          }
        }
        return String(max + 1).padStart(4, '0')
      },

      async addDoc () {
        if (!this.canRegister || this.registering) return
        this.registering = true
        const id = genId()
        const number = this.nextNumber(this.issuerCode, this.disciplineCode, this.docTypeCode)
        this.localRows.push({
          id,
          issuerCode: this.issuerCode,
          disciplineCode: this.disciplineCode,
          docTypeCode: this.docTypeCode,
          number,
        })
        this.emitRows()
        // Si adjuntaron un PDF en el alta, lo subimos a la fila recién creada.
        await this.uploadStaged(id)
        // Limpiamos disciplina y tipo; el emisor se conserva para altas seguidas.
        this.disciplineCode = ''
        this.docTypeCode = ''
        this.registering = false
      },

      // Adjunto previo al registro: guardamos el PDF y lo subimos al crear la fila.
      pickStaged () { this.$refs.stagedInput.click() },
      onStaged (e) {
        const f = e.target.files && e.target.files[0]
        e.target.value = ''
        if (!f) return
        if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) { this.$notify.error(this.$t('prm_summary_s02_pdf_only')); return }
        this.stagedFile = f
      },
      clearStaged () { this.stagedFile = null },
      async uploadStaged (rowId) {
        if (!this.stagedFile) return
        try {
          const ok = await FileUploader.submitFile(this.stagedFile, {
            entityType: 'prm_contract_file', entityId: 0, projectId: this.projectId,
            uploadEndpoint: `${this.baseUrl}/contract-files/doc_list/${rowId}`,
          })
          if (ok) this.$emit('files-changed')
          else this.$notify.error(this.$t('error_saving_data'))
        } catch (e) { this.$notify.error(this.$t('error_saving_data')) }
        this.stagedFile = null
      },

      removeRow (row) {
        const i = this.localRows.findIndex((r) => r.id === row.id)
        if (i < 0) return
        const file = this.fileFor(row)
        this.localRows.splice(i, 1)
        this.emitRows()
        if (file) this.$emit('remove-file', file.id)
      },

      addIssuer () {
        const name = this.newIssuerName.trim()
        if (!name) return
        const type = ISSUER_EXTERNAL_TYPES.find((t) => t.value === this.newIssuerType)
        const code = nextIssuerCode(this.localIssuers, type ? type.prefix : 'S')
        this.localIssuers.push({ id: genId(), type: this.newIssuerType, code, name })
        this.emitIssuers()
        this.newIssuerName = ''
      },

      removeIssuer (i) {
        const issuer = this.localIssuers[i]
        this.localIssuers.splice(i, 1)
        this.emitIssuers()
        // Al quitar un emisor externo, borramos también sus documentos (filas con ese código)
        // y los ficheros/versiones asociados a cada uno.
        const code = issuer && issuer.code
        if (!code) return
        const remaining = []
        let removedAny = false
        for (const r of this.localRows) {
          if (r.issuerCode === code) {
            const file = this.fileFor(r)
            if (file) this.$emit('remove-file', file.id)
            removedAny = true
          } else {
            remaining.push(r)
          }
        }
        if (removedAny) {
          this.localRows = remaining
          this.emitRows()
        }
      },

      async copyCode (row) {
        const code = this.fullCode(row)
        try {
          await navigator.clipboard.writeText(code)
          this.$notify.success(this.$t('prm_summary_s04_copied'))
        } catch (e) {
          this.$notify.error(this.$t('error_saving_data'))
        }
      },

      clearFilters () {
        this.search = ''
        this.filterIssuer = ''
        this.filterDiscipline = ''
        this.filterDocType = ''
      },

      emitRows () {
        this.$emit('save-list', { path: 's04.docList', items: this.localRows.map((r) => ({ ...r })) })
      },
      emitIssuers () {
        this.$emit('save-list', { path: 's04.issuers', items: this.localIssuers.map((e) => ({ ...e })) })
      },
    },
  }
</script>

<style lang="scss" scoped>
  // Cuerpo de la sección (dentro de la cabecera #e8eef5 de summary-card): padding y separación
  // entre bloques como el diseño (p-3 + space-y-4).
  .doc-list { padding: 12px; display: flex; flex-direction: column; gap: 16px; }

  // Paneles de controles: recuadro suave (uno gris para el alta de emisores, otro blanco para el alta
  // de documento), campos alineados abajo y con envoltura.
  .dl-panel {
    display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px;
    border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px;
  }
  .dl-panel.gray { background: #f8fafc; }

  // Campo (etiqueta + control).
  .dl-field { display: flex; flex-direction: column; gap: 4px; min-width: 150px; }
  .dl-field.grow { flex: 1 1 190px; }
  .dl-field.name { flex: 0 1 256px; }
  .dl-field label { font-size: 10px; font-weight: 600; color: #475569; }

  // Controles (select / input): sin altura fija, altura por padding (como el diseño).
  .dl-ctrl {
    width: 100%; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 4px;
    padding: 6px 8px; font-size: 11px; color: #334155; background: #fff; outline: none;
  }
  .dl-ctrl:focus { border-color: #3b82f6; }
  .dl-ctrl::placeholder { color: #94a3b8; }

  // Botones.
  .dl-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 4px; align-self: flex-end;
    border-radius: 4px; padding: 6px 12px; font-size: 10px; font-weight: 600; cursor: pointer;
    white-space: nowrap; border: 1px solid transparent;
  }
  .dl-btn.primary { background: #2f5f8f; color: #fff; }
  .dl-btn.primary:hover:not(:disabled) { background: #27527c; }
  .dl-btn.ghost { background: #fff; border-color: #9fb5cc; color: #2f5f8f; padding: 6px 10px; }
  .dl-btn.ghost:hover:not(:disabled) { background: #f6f9fc; }
  .dl-btn:disabled { opacity: .5; cursor: not-allowed; }

  // Adjunto previo (Adjuntar + nombre del PDF elegido).
  .dl-attach { align-self: flex-end; display: inline-flex; align-items: center; gap: 6px; }
  .dl-hidden { display: none; }
  .dl-staged { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; color: #475569; }
  .dl-staged-name { max-width: 130px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dl-staged-x { border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 0; line-height: 1; }
  .dl-staged-x:hover { color: #dc2626; }

  // Desplegable de emisores registrados (con borrar).
  .dl-reg { position: relative; align-self: flex-end; }
  .dl-backdrop { position: fixed; inset: 0; z-index: 40; }
  .dl-menu {
    position: absolute; top: calc(100% + 4px); right: 0; z-index: 41; min-width: 260px; max-height: 260px; overflow-y: auto;
    background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; box-shadow: 0 6px 20px rgba(30, 48, 70, .16); padding: 4px;
  }
  .dl-menu-item { display: flex; align-items: center; gap: 8px; padding: 5px 6px; border-radius: 4px; font-size: 11px; }
  .dl-menu-item:hover { background: #f4f6f9; }
  .dl-menu-code { font-weight: 700; color: #1e3046; }
  .dl-menu-name { color: #6b7787; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dl-trash { border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 2px; }
  .dl-trash:hover { color: #dc2626; }
  .dl-menu-empty { padding: 8px; text-align: center; color: #9aa4b2; font-size: 11px; }

  // Sub-card de filtros + tabla.
  .dl-subcard { border: 1px solid #b8c7d8; border-radius: 4px; overflow: hidden; }
  .dl-filter-bar { background: #edf2f7; border-bottom: 1px solid #b8c7d8; padding: 8px; display: flex; flex-direction: column; gap: 8px; }
  .dl-filter-grid {
    display: grid; align-items: end; gap: 8px;
    grid-template-columns: minmax(200px, 1.5fr) repeat(3, minmax(0, 1fr)) auto;
  }
  @media (max-width: 900px) { .dl-filter-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 560px) { .dl-filter-grid { grid-template-columns: 1fr; } }
  .dl-count { font-size: 10px; color: #64748b; margin: 0; }

  // Buscador con icono.
  .dl-search { position: relative; }
  .dl-search-ic { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; z-index: 1; }
  .dl-ctrl.srch { padding-left: 26px; }

  // Botón limpiar filtros.
  .dl-clear {
    display: inline-flex; align-items: center; justify-content: center; gap: 4px; align-self: flex-end;
    border: 1px solid #cbd5e1; background: #fff; color: #2f5f8f; border-radius: 4px; padding: 6px 8px;
    font-size: 10px; cursor: pointer;
  }
  .dl-clear:hover:not(:disabled) { background: #f6f9fc; }
  .dl-clear:disabled { opacity: .4; cursor: default; }

  // Tabla.
  .dl-table-scroll { overflow-x: auto; }
  table { width: 100%; min-width: 640px; border-collapse: collapse; font-size: 10px; }
  thead { background: #3d6b9a; }
  th { color: #fff; font-size: 10px; font-weight: 700; text-align: left; padding: 6px 8px; }
  td { padding: 8px 8px; font-size: 10px; color: #334155; vertical-align: middle; border-top: 1px solid #e2e8f0; }
  td.code { color: #33475f; }
  td.empty { text-align: center; color: #9aa4b2; padding: 14px; border-top: 0; }
  .hist-tr td { padding: 6px 10px; background: #fafbfc; }
  .doc-list.mark tbody td { background: #fff8e6; }

  // Acciones de fila (Copiar / Eliminar), como enlaces del diseño.
  .dl-copy { border: 0; background: transparent; color: #2f5f8f; cursor: pointer; font-size: 10px; margin-right: 8px; padding: 0; }
  .dl-copy:hover { text-decoration: underline; }
  .dl-del { display: inline-flex; align-items: center; gap: 4px; border: 0; background: transparent; color: #b91c1c; cursor: pointer; font-size: 10px; padding: 0; }
  .dl-del:hover { text-decoration: underline; }
  td.acts { white-space: nowrap; }
</style>
